import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Nav, Tab, Spinner, Dropdown, Button } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { fetchProfile } from '../../store/slices/profileSlice';
import { getCommunityByIdApi, getCommunityByIdWebApi, getCommunityDetailByIdWebApi, joinCommunityApi, getPendingRequestsApi, getCommunityMembersApi, getCommunityEventsApi, getCommunityLessonsApi, removeMembershipApi } from '../../api/communities';
import { storeTransaction } from '../../api/classes';
import { deleteEvent } from '../../api/events';
import { deleteLesson } from '../../api/lessons';
import { getTeacherByIdApi } from '../../api/teachers';
import { CourseCard } from '../marketplace/components/CourseCard';
import { CommunityFeed } from './components/CommunityFeed';
import { CommunityContent } from './components/CommunityContent';
import { CommunityMembers } from './components/CommunityMembers';
import { EventModal } from './components/EventModal';
import { LessonModal } from './components/LessonModal';
import DummyAvatar from '../../assets/images/avatar.png';
import './CommunityDetail.scss';

const getUTCEventDate = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const date = d.getUTCDate();

  let hours = 0;
  let minutes = 0;
  if (timeStr) {
    const parts = timeStr.split(':');
    hours = parseInt(parts[0], 10) || 0;
    minutes = parseInt(parts[1], 10) || 0;
  }
  return new Date(Date.UTC(year, month, date, hours, minutes));
};

const getEventStatus = (event) => {
  const dateStr = event.date || event.start_date || event.createdAt;
  if (!dateStr) return 'Upcoming';

  const startTimeStr = event.start_time || event.startTime || '00:00';
  const endTimeStr = event.end_time || event.endTime || '23:59';

  const startDate = getUTCEventDate(dateStr, startTimeStr);
  const endDate = getUTCEventDate(dateStr, endTimeStr);

  if (!startDate || !endDate) return 'Upcoming';

  const now = new Date();
  if (now < startDate) {
    return 'Upcoming';
  } else if (now >= startDate && now <= endDate) {
    return 'Live';
  } else {
    return 'Ended';
  }
};

export const CommunityDetail = () => {
  const location = useLocation();
  const id = location.state?.id;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { profile, loading: profileLoading, fetched: profileFetched, error: profileError } = useSelector((state) => state.profile);

  const hasToken = localStorage.getItem('authToken') && localStorage.getItem('authToken') !== 'null' && localStorage.getItem('authToken') !== 'undefined';

  const [communityData, setCommunityData] = useState(null);
  const [teacherData, setTeacherData] = useState(null);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const [showPaypal, setShowPaypal] = useState(false);
  const [activeTab, setActiveTab] = useState('feed');
  const [descExpanded, setDescExpanded] = useState(false);
  const [sidebarMembers, setSidebarMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [showLessonModal, setShowLessonModal] = useState(false);

  const community = communityData || {
    name: 'Loading Community...',
    description: 'Fetching details from server...',
    coverImage: '',
    type: 'free',
    subscriptionFee: 0,
    language: 'English',
    category: { name: 'General' },
    createdAt: new Date(),
    requestStatus: 'not_requested',
  };

  useEffect(() => {
    if (isAuthenticated && !profile && !profileLoading?.profile && !profileFetched && !profileError?.profile) {
      dispatch(fetchProfile());
    }
    fetchCommunity();
  }, [id, isAuthenticated, profile, profileLoading, profileFetched, profileError, dispatch]);

  const fetchCommunity = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Start independent requests concurrently
      const communityPromise = getCommunityByIdWebApi(id)
        .catch(() => getCommunityDetailByIdWebApi(id))
        .catch(() => getCommunityByIdApi(id));
      const membersPromise = getCommunityMembersApi(id, 'approved');
      const eventsPromise = getCommunityEventsApi(id);
      const lessonsPromise = getCommunityLessonsApi(id);

      // Wait for the community data first since dependent requests need it
      let res;
      try {
        res = await communityPromise;
      } catch (err) {
        // Handled below
      }

      const communityObj = res?.data?.community || res?.data || res?.community || (res?._id ? res : null);

      if (communityObj) {
        setCommunityData({
          ...communityObj,
          membersCount: res?.members?.approved || res?.membersCount || communityObj?.membersCount || 0,
          requestStatus: res?.requestStatus || communityObj?.requestStatus || 'not_requested',
          isPaidMember: res?.isPaidMember ?? communityObj?.isPaidMember ?? false
        });
        
        const ownerId = typeof communityObj.owner === 'object' ? communityObj.owner?._id : communityObj.owner;
        const ownerCheck = user && ownerId && (ownerId === user._id);

        // Fire dependent requests concurrently along with resolving the previously started independent ones
        const dependentPromises = [];
        if (ownerId) {
          dependentPromises.push(
            getTeacherByIdApi(ownerId)
              .then(tData => {
                if (tData) setTeacherData(tData?.data || tData?.teacher || tData);
              })
              .catch(() => {})
          );
        }
        if (ownerCheck) {
          dependentPromises.push(
            getPendingRequestsApi(id)
              .then(pendingRes => {
                const pendingList = pendingRes?.data || pendingRes?.requests || (Array.isArray(pendingRes) ? pendingRes : []);
                setPendingRequestsCount(pendingList.length || 0);
              })
              .catch(() => {})
          );
        }

        // Wait for all remaining requests (both dependent and independent) to settle
        await Promise.allSettled([
          ...dependentPromises,
          membersPromise.then(membersRes => setSidebarMembers(Array.isArray(membersRes) ? membersRes : (membersRes?.data || membersRes?.members || []))).catch(() => {}),
          eventsPromise.then(eventsRes => setEvents(eventsRes?.data || (Array.isArray(eventsRes) ? eventsRes : []))).catch(() => {}),
          lessonsPromise.then(lessonsRes => setLessons(lessonsRes?.data || (Array.isArray(lessonsRes) ? lessonsRes : []))).catch(() => {})
        ]);

      } else {
        setError('Community not found');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load community details');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!isAuthenticated) {
      // navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
      navigate('/login');//?redirect=' + encodeURIComponent(window.location.pathname));

      return;
    }
    try {
      setJoining(true);
      await joinCommunityApi(id, user._id);
      alert('Successfully requested to join!');
      fetchCommunity();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to join community');
      navigate('/login');
    } finally {
      setJoining(false);
    }
  };


  const handleShowPaypal = () => {
    if (!isAuthenticated) {
      // navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
      navigate('/login');//?redirect=' + encodeURIComponent(window.location.pathname));

      return;
    }
    setShowPaypal(true);
  };

  const handleLeave = async () => {
    if (!isAuthenticated) {
      // navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
      navigate('/login');//?redirect=' + encodeURIComponent(window.location.pathname));

      return;
    }
    if (window.confirm("Are you sure you want to leave this community?")) {
      try {
        setJoining(true);
        await removeMembershipApi(id, user._id);
        alert('You have left the community.');
        fetchCommunity();
      } catch (err) {
        alert(err.response?.data?.message || 'Failed to leave community');
      } finally {
        setJoining(false);
      }
    }
  };

  useEffect(() => {
    if (communityData) {
      const isOwner = user && communityData.owner && (communityData.owner._id === user._id || communityData.owner === user._id);
      const requestStatus = communityData.requestStatus || 'not_requested';
      const isPaidMember = communityData.isPaidMember;
      const hasAccess = isOwner || (requestStatus === 'approved' && (communityData.type === 'free' || isPaidMember));
      if (!hasAccess) {
        setActiveTab('about');
      }
    }
  }, [communityData, user]);

  if (error || (!loading && !communityData)) {
    return (
      <Container className="py-5 text-center">
        <div style={{ padding: '60px 20px' }}>
          <i className="bi bi-exclamation-circle d-block mb-3" style={{ fontSize: '3rem', color: '#6B7280' }}></i>
          <h5 className="fw-bold mb-2">{error || 'Community not found'}</h5>
          <Link to="/communities" className="btn rounded-pill px-4 py-2 fw-semibold text-white mt-2" style={{ backgroundColor: '#6C3BFF' }}>
            Back to Communities
          </Link>
        </div>
      </Container>
    );
  }

  const isOwner = user && community.owner && (community.owner._id === user._id || community.owner === user._id);
  const requestStatus = community.requestStatus || 'not_requested';
  const isPaidMember = community.isPaidMember;
  const hasAccess = isOwner || (requestStatus === 'approved' && (community.type === 'free' || isPaidMember));
  const categoryName = (community.category && typeof community.category === 'object')
    ? community.category.name
    : (community.categoryName || community.category || '');
  const membersCount = community.membersCount || 0;
  const handleDeleteEvent = async (eventId, e) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this class?")) {
      try {
        await deleteEvent(eventId);
        fetchCommunity(); // refresh data
      } catch (err) {
        alert("Failed to delete class.");
      }
    }
  };

  const handleDeleteLesson = async (lessonId, e) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this lesson?")) {
      try {
        await deleteLesson(lessonId);
        fetchCommunity(); // refresh data
      } catch (err) {
        alert("Failed to delete lesson.");
      }
    }
  };

  const displayMembers = sidebarMembers.slice(0, 9);
  const overflowCount = Math.max(0, membersCount - displayMembers.length);
  const description = community.description || '';
  const shortDesc = description.length > 220 ? description.slice(0, 220) + '...' : description;
  const rawTopics = community?.topics || community?.tags || [];
  const topics = rawTopics.slice(0, 5).map(t =>
    typeof t === 'string' ? { name: t, count: 0 } : { name: t.name || t.title || 'Topic', count: t.count || 0 }
  );

  const handleShare = async () => {
    const shareUrl = community?.shareUrlWeb || community?.shareUrl || window.location.href;
    const text = `Check out ${community?.name || 'this community'} on Yoospeak!`;

    if (navigator.share) {
      try {
        await navigator.share({ title: community?.name || 'Community', text, url: shareUrl });
      } catch (err) {
        console.log('Share failed:', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Share link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy link:', err);
      }
    }
  };

  return (
    <div className="community-detail-page py-4 position-relative">
      {loading && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center" style={{
          zIndex: 9999,
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          transition: 'all 0.3s ease-in-out'
        }}>
          <div className="p-4 rounded-4 shadow bg-white bg-opacity-75 text-center d-flex flex-column align-items-center" style={{ border: '1px solid rgba(255, 255, 255, 0.5)' }}>
            <Spinner animation="border" style={{ color: '#6C3BFF', width: '3rem', height: '3rem' }} />
            <span className="mt-3 fw-bold text-navy" style={{ letterSpacing: '0.5px' }}>Loading community...</span>
          </div>
        </div>
      )}
      <Container fluid="xl">
        <section className="profile-banner-section bg-white border rounded-4 overflow-hidden">
          {/* ── Banner ── */}
          <div className="cd-banner">
            <img
              src={community.coverImage || 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&h=400&fit=crop'}
              alt={community.name}
              className="cd-banner__img"
              onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&h=400&fit=crop'; }}
            />
            <div className="cd-banner__actions">
              <button className="cd-btn-share" onClick={handleShare}>
                <i className="bi bi-share"></i> Share
              </button>
            </div>
          </div>

          {/* ── Community Info Card ── */}
          <div className="cd-info-card bg-white p-4">
            {/* Top row: logo + name + actions */}
            <div className="cd-info-top">
              {/* Logo */}
              <div className="cd-logo-wrap">
                <img
                  src={community.coverImage || community.logo || DummyAvatar}
                  alt={community.name}
                  className="cd-logo border border-3 border-white rounded-circle object-fit-cover shadow"
                  onError={(e) => { e.target.src = DummyAvatar; }}
                />
              </div>

              {/* Name + subtitle + tags */}
              <div className="cd-info-body">
                <div className="cd-name text-dark mb-1">{community.name}</div>
                <p className="cd-subtitle">{community.owner?.full_name ? `${community.owner.full_name} ${community.name}` : community.name}</p>
                <div className="cd-tags">
                  {community.language && (
                    <span className="cd-tag cd-tag--lang">
                      <i className="bi bi-translate"></i> {community.language}
                    </span>
                  )}
                  <span className="cd-tag cd-tag--students">
                    <i className="bi bi-people-fill"></i> {membersCount} Students
                  </span>
                  {categoryName && (
                    <span className="cd-tag cd-tag--cat">
                      <i className="bi bi-chat-dots-fill"></i> {categoryName}
                    </span>
                  )}
                </div>
              </div>

              {/* Joined / Manage button */}
              <div className="cd-info-actions">
                {isOwner ? (
                  <div className="d-flex gap-2 align-items-center">
                    <Link to="/edit-community" state={{ id: community._id }}>
                      <button className="cd-btn-manage">
                        <i className="bi bi-pencil-square me-1"></i> Manage
                      </button>
                    </Link>
                    {pendingRequestsCount > 0 && (
                      <span
                        className="badge rounded-pill fw-semibold px-3 py-2"
                        style={{ backgroundColor: '#6C3BFF', color: '#fff', fontSize: '0.78rem', cursor: 'pointer' }}
                        onClick={() => setActiveTab('requests')}
                        title="View Pending Requests"
                      >
                        {pendingRequestsCount} Requests
                      </span>
                    )}
                  </div>
                ) : showPaypal ? (
                  <div className="paypal-btn-container" style={{ minWidth: '200px' }}>
                    <PayPalScriptProvider options={{ "client-id": "AcEBlYxSTDKF51XKtEB_L4-X18Q4kpoyUj0yY6DggYqVCssqh9nJbcUESyx6Fp_HLJnPy3msNxmjttaG", currency: "USD" }}>
                      <PayPalButtons
                        style={{ layout: "horizontal", height: 38 }}
                        createOrder={(data, actions) => {
                          return actions.order.create({
                            purchase_units: [{
                              amount: { value: (community.subscriptionFee || 0).toString() }
                            }]
                          });
                        }}
                        onApprove={(data, actions) => {
                          return actions.order.capture().then(async (details) => {
                            try {
                              setJoining(true);
                              await storeTransaction({
                                amount: community.subscriptionFee || 0,
                                status: 'succeeded',
                                currency: 'usd',
                                charge_id: details.id,
                                payment_method: 'paypal',
                                transaction_type: 'community_enrollment'
                              });
                              await joinCommunityApi(id, user._id, { payment_method: 'paypal', transaction_id: details.id });
                              alert('Payment successful and request processed!');
                              setShowPaypal(false);
                              fetchCommunity();
                            } catch (err) {
                              console.error(err);
                              alert(err?.response?.data?.message || 'Payment successful but failed to join community.');
                            } finally {
                              setJoining(false);
                            }
                          });
                        }}
                        onCancel={() => setShowPaypal(false)}
                        onError={(err) => {
                          console.error("PayPal Error:", err);
                          alert("Payment failed or was cancelled.");
                          setShowPaypal(false);
                        }}
                      />
                    </PayPalScriptProvider>
                  </div>
                ) : (
                  <>
                    {(community.type === 'free' || !community.type) && (
                      <>
                        {requestStatus === 'not_requested' && (
                          <Button className="cd-btn-joined border-0" onClick={handleJoin} disabled={joining}>
                            {joining ? 'Processing...' : 'Free Community – Request to Join'}
                          </Button>
                        )}
                        {requestStatus === 'pending' && (
                          <Button variant="outline-secondary" className="cd-btn-joined border-0" disabled>
                            Request Sent
                          </Button>
                        )}
                        {requestStatus === 'approved' && (
                          <Dropdown>
                            <Dropdown.Toggle className="cd-btn-joined" id="cd-join-dropdown">
                              <i className="bi bi-check-lg me-1"></i> Joined
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="shadow border-0 rounded-3 mt-1" style={{ minWidth: '180px' }}>
                              <Dropdown.Item style={{ fontSize: '0.875rem', color: '#6B7280' }} onClick={handleLeave} disabled={joining}>
                                <i className="bi bi-box-arrow-right me-2"></i>
                                {joining ? 'Processing...' : 'Leave Community'}
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        )}
                      </>
                    )}

                    {community.type === 'freetrial' && (
                      <>
                        {requestStatus === 'not_requested' && (
                          <Button className="cd-btn-joined border-0" onClick={handleJoin} disabled={joining}>
                            {joining ? 'Processing...' : `Start ${community.trialDuration || 7}-Day Free Trial`}
                          </Button>
                        )}
                        {requestStatus === 'pending' && (
                          <Button variant="outline-secondary" className="cd-btn-joined border-0" disabled>
                            Request Sent
                          </Button>
                        )}
                        {requestStatus === 'approved' && !isPaidMember && (
                          <Button className="cd-btn-joined border-0" onClick={handleShowPaypal}>
                            {`Trial Ended – Pay $${community.subscriptionFee}`}
                          </Button>
                        )}
                        {requestStatus === 'approved' && isPaidMember && (
                          <Dropdown>
                            <Dropdown.Toggle className="cd-btn-joined" id="cd-join-dropdown">
                              <i className="bi bi-check-lg me-1"></i> Joined
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="shadow border-0 rounded-3 mt-1" style={{ minWidth: '180px' }}>
                              <Dropdown.Item style={{ fontSize: '0.875rem', color: '#6B7280' }} onClick={handleLeave} disabled={joining}>
                                <i className="bi bi-box-arrow-right me-2"></i>
                                {joining ? 'Processing...' : 'Leave Community'}
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        )}
                      </>
                    )}

                    {community.type === 'monthly' && (
                      <>
                        {requestStatus === 'not_requested' && (
                          <Button className="cd-btn-joined border-0" onClick={handleShowPaypal}>
                            {`Start Monthly Subscription ($${community.subscriptionFee}/month)`}
                          </Button>
                        )}
                        {requestStatus === 'pending' && (
                          <Button variant="outline-secondary" className="cd-btn-joined border-0" disabled>
                            Request Sent
                          </Button>
                        )}
                        {requestStatus === 'approved' && !isPaidMember && (
                          <Button className="cd-btn-joined border-0" onClick={handleShowPaypal}>
                            {`Subscribe Monthly ($${community.subscriptionFee}/month)`}
                          </Button>
                        )}
                        {requestStatus === 'approved' && isPaidMember && (
                          <Dropdown>
                            <Dropdown.Toggle className="cd-btn-joined" id="cd-join-dropdown">
                              <i className="bi bi-check-lg me-1"></i> Joined
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="shadow border-0 rounded-3 mt-1" style={{ minWidth: '180px' }}>
                              <Dropdown.Item style={{ fontSize: '0.875rem', color: '#6B7280' }} onClick={handleLeave} disabled={joining}>
                                <i className="bi bi-box-arrow-right me-2"></i>
                                {joining ? 'Processing...' : 'Leave Community'}
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        )}
                      </>
                    )}

                    {community.type === 'onetime' && (
                      <>
                        {requestStatus === 'not_requested' && (
                          <Button className="cd-btn-joined border-0" onClick={handleShowPaypal}>
                            {`Pay $${community.subscriptionFee} – One-Time Payment`}
                          </Button>
                        )}
                        {requestStatus === 'pending' && (
                          <Button variant="outline-secondary" className="cd-btn-joined border-0" disabled>
                            Request Sent
                          </Button>
                        )}
                        {requestStatus === 'approved' && !isPaidMember && (
                          <Button className="cd-btn-joined border-0" onClick={handleShowPaypal}>
                            {`Pay $${community.subscriptionFee} – One-Time Payment`}
                          </Button>
                        )}
                        {requestStatus === 'approved' && isPaidMember && (
                          <Dropdown>
                            <Dropdown.Toggle className="cd-btn-joined" id="cd-join-dropdown">
                              <i className="bi bi-check-lg me-1"></i> Joined
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="shadow border-0 rounded-3 mt-1" style={{ minWidth: '180px' }}>
                              <Dropdown.Item style={{ fontSize: '0.875rem', color: '#6B7280' }} onClick={handleLeave} disabled={joining}>
                                <i className="bi bi-box-arrow-right me-2"></i>
                                {joining ? 'Processing...' : 'Leave Community'}
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="cd-description">
              <p className="cd-desc-text">
                {descExpanded ? description : shortDesc}
              </p>

              {description.length > 220 && (
                <button className="cd-read-more" onClick={() => setDescExpanded(!descExpanded)}>
                  {descExpanded ? 'Show Less' : 'Read More'}
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="mt-3">
          {/* ── Tab Navigation ── */}
          <div className="cd-tab-nav">
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <Nav as="div" className="cd-tabs">

                {hasAccess && (
                  <Nav.Item>
                    <Nav.Link eventKey="feed" className="cd-tab">
                      <i className="bi bi-card-text"></i> Post
                    </Nav.Link>
                  </Nav.Item>
                )}

                <Nav.Item>
                  <Nav.Link eventKey="about" className="cd-tab">
                    <i className="bi bi-info-circle"></i> About
                  </Nav.Link>
                </Nav.Item>

                {hasAccess && (
                  <Nav.Item>
                    <Nav.Link eventKey="courses" className="cd-tab">
                      <i className="bi bi-mortarboard"></i> Courses
                    </Nav.Link>
                  </Nav.Item>
                )}

                {hasAccess && (
                  <Nav.Item>
                    <Nav.Link eventKey="classes" className="cd-tab">
                      <i className="bi bi-calendar3"></i> Classes
                    </Nav.Link>
                  </Nav.Item>
                )}

                {hasAccess && (
                  <Nav.Item>
                    <Nav.Link eventKey="liveroom" className="cd-tab cd-tab--live">
                      <i className="bi bi-broadcast"></i> Join Room
                    </Nav.Link>
                  </Nav.Item>
                )}

                {hasAccess && (
                  <Nav.Item>
                    <Nav.Link eventKey="members" className="cd-tab">
                      <i className="bi bi-people"></i> Members
                    </Nav.Link>
                  </Nav.Item>
                )}

                {isOwner && (
                  <Nav.Item>
                    <Nav.Link eventKey="requests" className="cd-tab">
                      <i className="bi bi-person-plus"></i> Requests
                      {pendingRequestsCount > 0 && <span className="cd-badge-count">{pendingRequestsCount}</span>}
                    </Nav.Link>
                  </Nav.Item>
                )}
              </Nav>

              {/* ── Tab Content ── */}
              <div className="cd-content mt-3">
                <Tab.Content>

                  {/* FEED / POST tab */}
                  <Tab.Pane eventKey="feed">
                    <Row className="g-4">
                      {/* Left: Activity */}
                      <Col lg={8}>
                        <div className="bg-white bg-opacity-75 rounded-4 p-3">
                          <div className="cd-activity-header d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2 px-3">
                            <span className="cd-activity-title">Activity</span>
                            <span className="cd-activity-sort">Most Recent <i className="bi bi-chevron-down"></i></span>
                          </div>
                          <CommunityFeed communityId={id} isOwner={isOwner} />
                        </div>
                      </Col>

                      {/* Right: Sidebar */}
                      <Col lg={4}>
                        <SidebarAbout community={community} categoryName={categoryName} description={description} />
                        <SidebarMembers membersCount={membersCount} displayMembers={displayMembers} overflowCount={overflowCount} onSeeAll={() => setActiveTab('members')} />
                        {topics.length > 0 && <SidebarTopics topics={topics} />}
                      </Col>
                    </Row>
                  </Tab.Pane>

                  {/* ABOUT tab */}
                  <Tab.Pane eventKey="about">
                    <Row className="g-4">
                      <Col lg={8}>
                        <div className="cd-panel">
                          <h5 className="cd-panel__title">About this Community</h5>
                          <p className="cd-panel__text">{description || `Welcome to ${community.name}!`}</p>
                          <hr className="cd-divider" />
                          <div className="cd-about-grid">
                            {[
                              { icon: 'bi-grid', label: 'Category', value: categoryName || 'General' },
                              { icon: 'bi-translate', label: 'Language', value: community.language || 'English' },
                              { icon: 'bi-people-fill', label: 'Members', value: `${membersCount} Students` },
                              { icon: 'bi-tag-fill', label: 'Price', value: community.subscriptionFee > 0 ? `$${community.subscriptionFee}/month` : 'Free Community' },
                              { icon: 'bi-clock', label: 'Created', value: community.createdAt ? new Date(community.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unknown' },
                              { icon: 'bi-lock', label: 'Privacy', value: community.subscriptionFee > 0 ? 'Private' : 'Public' },
                            ].map((item, i) => (
                              <div key={i} className="cd-about-item">
                                <div className="cd-about-item__icon"><i className={`bi ${item.icon}`}></i></div>
                                <div>
                                  <div className="cd-about-item__label">{item.label}</div>
                                  <div className="cd-about-item__value">{item.value}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Col>
                      <Col lg={4}>
                        <SidebarMembers membersCount={membersCount} displayMembers={displayMembers} overflowCount={overflowCount} onSeeAll={() => setActiveTab('members')} />
                      </Col>
                    </Row>
                  </Tab.Pane>

                  {/* LIVE ROOM tab */}
                  <Tab.Pane eventKey="liveroom">
                    <div className="cd-panel">
                      <CommunityContent communityId={id} isOwner={isOwner} user={user} community={community} />
                    </div>
                  </Tab.Pane>

                  {/* COURSES tab */}
                  <Tab.Pane eventKey="courses">
                    <div className="cd-panel">
                      {isOwner && (
                        <div className="d-flex justify-content-end mb-3">
                          <Button variant="primary" className="rounded-pill" onClick={() => setShowLessonModal(true)}>
                            <i className="bi bi-plus-lg me-1"></i> Add Course
                          </Button>
                        </div>
                      )}
                      {(!lessons || lessons.length === 0) ? (
                        <EmptyState
                          icon="bi-journal-bookmark"
                          title="No Courses Yet"
                          desc="No courses are linked to this community."
                        />
                      ) : (
                        <Row className="g-4 row-cols-1 row-cols-md-2 row-cols-lg-3">
                          {lessons.map(lesson => (
                            <Col key={lesson._id}>
                              <div className="cd-class-card h-100 d-flex flex-column position-relative border-0 shadow-sm" onClick={() => navigate('/lesson-detail', { state: { id: lesson._id } })} style={{ cursor: 'pointer' }}>
                                <div className="cd-class-card__head position-relative" style={{ height: '110px' }}>
                                  <div className="position-absolute bottom-0 w-100 overflow-hidden" style={{ lineHeight: 0 }}>
                                    <svg viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '100%', height: 'auto' }}>
                                      <path fill="#ffffff" fillOpacity="1" d="M0,160L48,170.7C96,181,192,203,288,197.3C384,192,480,160,576,144C672,128,768,128,864,154.7C960,181,1056,235,1152,245.3C1248,256,1344,224,1392,208L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
                                    </svg>
                                  </div>
                                  <div className="position-absolute top-0 w-100 p-3 d-flex justify-content-between align-items-start z-1">
                                    <span className="cd-class-type shadow-sm">Course</span>
                                    {isOwner && (
                                      <div className="d-flex gap-2">
                                        <button
                                          className="cd-action-btn rounded-circle shadow-sm text-danger"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteLesson(lesson._id, e);
                                          }}
                                          title="Delete Course"
                                        >
                                          <i className="bi bi-trash"></i>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="cd-class-card__body d-flex flex-column flex-grow-1 position-relative p-4 pt-4">
                                  {/* Avatar overlapping head and body */}
                                  <div className="cd-class-avatar position-absolute rounded-circle shadow-sm bg-white p-1 d-flex align-items-center justify-content-center" style={{ width: '56px', height: '56px', top: '-28px', left: '1.5rem', zIndex: 2 }}>
                                    <i className="bi bi-play-btn-fill fs-3 text-primary"></i>
                                  </div>

                                  <div className="d-flex align-items-center gap-2 mb-2" style={{ marginTop: '1rem' }}>
                                    <div className="rounded-circle bg-primary" style={{ width: '8px', height: '8px' }}></div>
                                    <span className="text-uppercase fw-bold text-xs tracking-wider" style={{ color: '#0d6efd', letterSpacing: '0.05em' }}>Course</span>
                                  </div>
                                  <h3 className="cd-class-title fw-bold text-navy fs-5 mb-2">{lesson.title}</h3>
                                  <p className="text-muted text-sm text-truncate-2 mb-4">{lesson.description || 'Learn and grow with this amazing course.'}</p>

                                  <button className="btn btn-outline-primary w-100 mt-auto rounded-pill py-2 fw-semibold d-flex align-items-center justify-content-center gap-2" onClick={(e) => { e.stopPropagation(); navigate('/lesson-detail', { state: { id: lesson._id } }); }}>
                                    <span>Start Course</span>
                                    <i className="bi bi-arrow-right"></i>
                                  </button>
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </div>
                  </Tab.Pane>

                  {/* CLASSES tab */}
                  <Tab.Pane eventKey="classes">
                    <div className="cd-panel">
                      {isOwner && (
                        <div className="d-flex justify-content-end mb-3">
                          <Button variant="primary" className="rounded-pill" onClick={() => {
                            if (profile) {
                              if (profile.isSubscriptionEnabled === true && profile.has_active_subscription !== true) {
                                navigate('/profile?tab=subscriptions');
                                return;
                              }
                            }
                            setEventToEdit(null);
                            setShowEventModal(true);
                          }}>
                            <i className="bi bi-plus-lg me-1"></i> Add Class
                          </Button>
                        </div>
                      )}
                      {(!events || events.length === 0) ? (
                        <EmptyState
                          icon="bi-calendar3"
                          title="No Classes Yet"
                          desc="No classes are scheduled for this community."
                        />
                      ) : (
                        <Row className="g-4 row-cols-1 row-cols-md-2 row-cols-lg-3">
                          {events.map(event => (
                            <Col key={event._id}>
                              <div className="cd-class-card h-100 d-flex flex-column position-relative border-0 shadow-sm">
                                <div className="cd-class-card__head position-relative" style={{ height: '110px' }}>
                                  <div className="position-absolute bottom-0 w-100 overflow-hidden" style={{ lineHeight: 0 }}>
                                    <svg viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '100%', height: 'auto' }}>
                                      <path fill="#ffffff" fillOpacity="1" d="M0,160L48,170.7C96,181,192,203,288,197.3C384,192,480,160,576,144C672,128,768,128,864,154.7C960,181,1056,235,1152,245.3C1248,256,1344,224,1392,208L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
                                    </svg>
                                  </div>
                                  <div className="position-absolute top-0 w-100 p-3 d-flex justify-content-between align-items-start z-1">
                                    <span className="cd-class-type shadow-sm">{event.eventType === 'one_to_one' ? '1-on-1' : (event.eventType === 'paid' ? 'Paid' : 'Free')}</span>
                                    {isOwner && (
                                      <div className="d-flex gap-2">
                                        <button
                                          className="cd-action-btn rounded-circle shadow-sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEventToEdit(event);
                                            setShowEventModal(true);
                                          }}
                                          title="Edit Class"
                                        >
                                          <i className="bi bi-pencil"></i>
                                        </button>
                                        <button
                                          className="cd-action-btn rounded-circle shadow-sm text-danger"
                                          onClick={(e) => handleDeleteEvent(event._id, e)}
                                          title="Delete Class"
                                        >
                                          <i className="bi bi-trash"></i>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="cd-class-card__body mt-auto bg-white flex-grow-1 d-flex flex-column pt-0 px-4 pb-4 position-relative z-1">
                                  <div className="cd-class-info d-flex align-items-start mb-4 position-relative" style={{ marginTop: '-28px' }}>
                                    <div className="cd-class-icon-circle bg-purple text-white rounded-circle d-flex justify-content-center align-items-center me-3 shadow-sm border border-2 border-white" style={{ width: '56px', height: '56px', flexShrink: 0 }}>
                                      <i className="bi bi-calendar3 fs-4"></i>
                                    </div>
                                    <div className="pt-4 mt-2">
                                      <h3 className="cd-class-title mb-1 text-truncate" title={event.title}>{event.title}</h3>
                                      {(() => {
                                        const status = getEventStatus(event);
                                        if (status === 'Live') {
                                          return (
                                            <span className="badge rounded-pill fw-medium d-inline-flex align-items-center gap-2 px-2 py-1 mt-1" style={{ fontSize: '0.75rem', color: '#dc3545', backgroundColor: '#fdf2f2' }}>
                                              <div className="rounded-circle bg-danger" style={{ width: '6px', height: '6px' }}></div> Live Class
                                            </span>
                                          );
                                        } else if (status === 'Ended') {
                                          return (
                                            <span className="badge rounded-pill fw-medium d-inline-flex align-items-center gap-2 px-2 py-1 mt-1" style={{ fontSize: '0.75rem', color: '#6c757d', backgroundColor: '#f8f9fa' }}>
                                              <div className="rounded-circle bg-secondary" style={{ width: '6px', height: '6px' }}></div> Ended Class
                                            </span>
                                          );
                                        } else {
                                          return (
                                            <span className="badge bg-light text-purple rounded-pill fw-medium d-inline-flex align-items-center gap-2 px-2 py-1 mt-1" style={{ fontSize: '0.75rem' }}>
                                              <div className="rounded-circle bg-purple" style={{ width: '6px', height: '6px' }}></div> Upcoming Class
                                            </span>
                                          );
                                        }
                                      })()}
                                    </div>
                                  </div>
                                  {(() => {
                                    const dateVal = event.startDate || event.start_date || event.date || event.createdAt;
                                    const endVal = event.endDate || event.end_date;

                                    const localStart = dateVal ? new Date(dateVal) : null;
                                    const localEnd = endVal ? new Date(endVal) : null;

                                    const isValidStart = localStart && !isNaN(localStart.getTime());
                                    const isValidEnd = localEnd && !isNaN(localEnd.getTime());

                                    const dateText = isValidStart
                                      ? localStart.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })
                                      : 'TBD';

                                    const startTimeText = isValidStart
                                      ? localStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                      : (event.start_time || event.startTime || 'TBD');

                                    const endTimeText = isValidEnd
                                      ? localEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                      : (event.end_time || event.endTime || 'TBD');

                                    return (
                                      <div className="cd-class-meta mb-4">
                                        <span className="text-muted">
                                          <i className="bi bi-calendar3"></i> {dateText}
                                        </span>
                                        <span className="text-muted">
                                          <i className="bi bi-clock"></i> {startTimeText} – {endTimeText}
                                        </span>
                                      </div>
                                    );
                                  })()}
                                  <button className="cd-btn-book d-flex justify-content-between align-items-center mt-auto w-100" onClick={() => navigate('/event-detail', { state: { id: event._id } })}>
                                    <span className="flex-grow-1 text-center ms-3">View Class</span>
                                    <i className="bi bi-arrow-right"></i>
                                  </button>
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </div>
                  </Tab.Pane>


                  {/* MEMBERS tab */}
                  <Tab.Pane eventKey="members">
                    <div className="cd-panel">
                      <CommunityMembers communityId={id} isOwner={isOwner} showOnly="members" />
                    </div>
                  </Tab.Pane>

                  {/* REQUESTS tab */}
                  {isOwner && (
                    <Tab.Pane eventKey="requests">
                      <div className="cd-panel">
                        <CommunityMembers communityId={id} isOwner={isOwner} showOnly="pending" />
                      </div>
                    </Tab.Pane>
                  )}

                </Tab.Content>
              </div>
            </Tab.Container>
          </div>
        </section>
      </Container>
      <EventModal
        show={showEventModal}
        onHide={() => setShowEventModal(false)}
        communityId={id}
        eventToEdit={eventToEdit}
        onSuccess={fetchCommunity}
      />
      <LessonModal
        show={showLessonModal}
        onHide={() => setShowLessonModal(false)}
        communityId={id}
        onSuccess={fetchCommunity}
      />
    </div>
  );
};

/* ─── Small sidebar sub-components ─── */

const SidebarAbout = ({ community, categoryName, description }) => (
  <div className="cd-widget">
    <div className="cd-widget__head">
      <h6 className="cd-widget__title">About Community</h6>
    </div>
    <p className="cd-widget__desc">
      {description.slice(0, 160) || `Welcome to ${community?.name}! A friendly space to learn, share, and grow together.`}
    </p>
    {description.length > 160 && (
      <button className="cd-read-more mb-3">Read More</button>
    )}
    <div className="cd-meta-list">
      <div className="cd-meta-row">
        <span className="cd-meta-label"><i className="bi bi-grid"></i> Category</span>
        <span className="cd-meta-value">{categoryName || 'General'}</span>
      </div>
      {community?.language && (
        <div className="cd-meta-row">
          <span className="cd-meta-label"><i className="bi bi-translate"></i> Language</span>
          <span className="cd-meta-value">{community.language}</span>
        </div>
      )}
      <div className="cd-meta-row">
        <span className="cd-meta-label"><i className="bi bi-clock"></i> Created</span>
        <span className="cd-meta-value">
          {community?.createdAt ? new Date(community.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unknown'}
        </span>
      </div>
      <div className="cd-meta-row">
        <span className="cd-meta-label"><i className="bi bi-lock"></i> Privacy</span>
        <span className="cd-meta-value">{community?.subscriptionFee > 0 ? 'Private' : 'Public'}</span>
      </div>
    </div>
  </div>
);

const SidebarMembers = ({ membersCount, displayMembers, overflowCount, onSeeAll }) => (
  <div className="cd-widget">
    <div className="cd-widget__head">
      <h6 className="cd-widget__title">Members ({membersCount})</h6>
      <button className="cd-widget__see-all" onClick={onSeeAll}>See All</button>
    </div>
    <div className="cd-members-grid">
      {displayMembers.length > 0 ? (
        <>
          {displayMembers.map((m, i) => (
            <img
              key={i}
              src={m.user?.profile_picture || DummyAvatar}
              alt={m.user?.full_name || 'Member'}
              title={m.user?.full_name}
              className="cd-member-avatar"
              onError={(e) => { e.target.src = DummyAvatar; }}
            />
          ))}
          {/* Show + bubble only when total members > 9 */}
          {overflowCount > 0 && (
            <div className="cd-members-more">+{overflowCount}</div>
          )}
        </>
      ) : (
        <p className="cd-empty-text">No members yet.</p>
      )}
    </div>
  </div>
);

const SidebarTopics = ({ topics }) => (
  <div className="cd-widget">
    <div className="cd-widget__head">
      <h6 className="cd-widget__title">Popular Topics</h6>
      <button className="cd-widget__see-all">See All</button>
    </div>
    <div className="cd-topics-list">
      {topics.map((t, i) => (
        <div key={i} className="cd-topic-item">
          <div className="cd-topic-hash">#</div>
          <div>
            <div className="cd-topic-name">{t.name}</div>
            {t.count > 0 && <div className="cd-topic-count">{t.count} posts</div>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const EmptyState = ({ icon, title, desc, action }) => (
  <div className="cd-empty-state">
    <i className={`bi ${icon}`}></i>
    <h5>{title}</h5>
    <p>{desc}</p>
    {action && <div>{action}</div>}
  </div>
);
